import { html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { choose } from "lit/directives/choose.js";
import { classMap } from "lit/directives/class-map.js";
import { when } from "lit/directives/when.js";

import { toRomanNumeral } from "@/helpers/numbers";
import { getLetterFromAlphabet } from "@/helpers/strings";
import type { TextSize } from "@/internals/size";
import type { Theme } from "@/internals/theme";

import type { ListType } from "@/components/display/atlas-list/types";
import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import styles from "./atlas-list-item.scss";

export type ListItemProps = AtlasElementProps & {
    "icon": string;
    "icon-gap": "default" | "large";
    "icon-theme": Theme;
    "size": TextSize;
    "disabled": boolean;
};

/**
 * @slot - Usado para o conteúdo do item da lista
 * @slot icon-start - Usado para um ícone no início do item
 * @slot icon-end - Usado para um ícone no final do item
 *
 * @prop {boolean} clickable - Propriedade que define se o item é clicável
 * @prop {boolean} selected - Propriedade que define se o item está selecionado
 * @prop {boolean} disabled - Propriedade que define se o item está desabilitado
 * @prop {string} icon - Propriedade que define o nome do ícone a ser exibido
 * @prop {string} iconPosition - Propriedade que define a posição do ícone (start ou end)
 *
 * @tag atlas-list-item
 */
@customElement("atlas-list-item")
export default class AtlasListItem extends AtlasElement {
    static styles = styles;

    /** Define o nome do ícone a ser exibido */
    @property({ type: String }) icon: string;

    /** Define o espaçamento entre o ícone e o texto */
    @property({ type: String, attribute: "icon-gap" }) iconGap: "default" | "large" = "default";

    /** Define o tema do ícone */
    @property({ type: String, attribute: "icon-theme" }) iconTheme: Theme;

    /** O tamanho do ícone e texto desejados */
    @property({ type: String }) size: TextSize = "md";

    /** Define se o item está desabilitado */
    @property({ type: Boolean, reflect: true }) disabled = false;

    /**
     * @internal
     * O tipo de lista que o item pertence
     */
    @property({ type: String, attribute: null }) listType: ListType = "default";

    /**
     * @internal
     * O nível de aninhamento da lista que o item pertence
     */
    @property({ type: Number, attribute: null }) listNestedLevel = 0;

    /**
     * @internal
     * O alinhamento do marcador dos itens da lista
     */
    @property({ type: String, attribute: "marker-alignment", reflect: true }) markerAlignment: "left" | "right" =
        "left";

    /** @internal */
    public connectedCallback() {
        if (!this.hasAttribute("role")) {
            this.setAttribute("role", "listitem");
        }

        super.connectedCallback();
    }

    private getItemIndex() {
        const parentItems = Array.from(this.parentElement.querySelectorAll("atlas-list-item"));
        return parentItems.indexOf(this);
    }

    private renderMarker() {
        return choose(
            this.listType,
            [
                ["ordered", () => this.renderOrderedListMarker()],
                ["unordered", () => this.renderUnorderedListMarker()]
            ],
            () => this.renderIconMarker()
        );
    }

    private renderOrderedListMarker() {
        const itemIndex = this.getItemIndex();
        const indexType = this.listNestedLevel % 3;
        let marker = "";

        if (indexType === 0) {
            marker = (itemIndex + 1).toString();
        } else if (indexType === 1) {
            marker = getLetterFromAlphabet(itemIndex);
        } else {
            marker = toRomanNumeral(itemIndex + 1);
        }

        return html`<span class="marker">${marker}.</span>`;
    }

    private renderUnorderedListMarker() {
        const indexType = this.listNestedLevel % 3;
        let marker = "";

        if (indexType === 0) {
            marker = "●";
        } else if (indexType === 1) {
            marker = "○";
        } else {
            marker = "■";
        }

        return html`<span class="marker">${marker}</span>`;
    }

    private renderIconMarker() {
        return when(
            !!this.icon,
            () => html`
                <atlas-icon class="marker" name="${this.icon}" size="auto" theme="${this.iconTheme}"></atlas-icon>
            `
        );
    }

    /** @internal */
    public render() {
        const itemContentClass = {
            "item-content": true,
            "has-marker": !!this.icon || this.listType === "ordered" || this.listType === "unordered"
        };

        return html`
            ${this.renderMarker()}
            <div class=${classMap(itemContentClass)}>
                <atlas-text size="${this.size}">
                    <slot></slot>
                </atlas-text>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-list-item": AtlasListItem;
    }
}
